import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard, Plus, RefreshCw,
  BarChart2, Users, Activity, CreditCard, BookOpen,
  Bell, Monitor, ChevronRight, Clock, Shield,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  FioriFilterBar,
  FioriTable,
  FioriStatusBadge,
} from "@/components/fiori";
import type { FioriColumn } from "@/components/fiori";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import DashboardView from "./DashboardView";

const DASHBOARD_ICONS: Record<string, React.ElementType> = {
  "Executive Operations Overview": BarChart2,
  "Access & Approval Performance": Users,
  "Checkpoint Operations": Shield,
  "Contractor & Visitor Intelligence": Activity,
  "Card Management Overview": CreditCard,
  "Knowledge & Compliance": BookOpen,
  "Security Alert Intelligence": Bell,
  "NOC Big Screen": Monitor,
};

const REFRESH_LABELS: Record<string, string> = {
  "Executive Operations Overview": "5 min",
  "Access & Approval Performance": "5 min",
  "Checkpoint Operations": "1 min",
  "Contractor & Visitor Intelligence": "15 min",
  "Card Management Overview": "10 min",
  "Knowledge & Compliance": "30 min",
  "Security Alert Intelligence": "2 min",
  "NOC Big Screen": "30 sec",
};

type Dashboard = {
  id: number;
  name: string;
  description: string | null;
  isSystem: boolean;
  createdAt: Date;
};

interface DashboardGalleryProps {
  canCreate?: boolean;
  onOpenDashboard?: (id: number) => void;
  /** Extra rows prepended to the System Dashboards list (e.g. the Ticketing dashboard) */
  prependSystem?: Dashboard[];
}

export default function DashboardGallery({ canCreate = false, onOpenDashboard, prependSystem = [] }: DashboardGalleryProps) {
  const { t } = useTranslation();
  const [selectedDashboardId, setSelectedDashboardId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | "system" | "custom">("all");

  // New Dashboard dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");

  const { data: dashboards, isLoading, refetch, isRefetching } = trpc.analytics.dashboards.list.useQuery(
    undefined,
    { refetchOnWindowFocus: false }
  );

  const utils = trpc.useUtils();
  const createDashboard = trpc.analytics.dashboards.create.useMutation({
    onSuccess: (data) => {
      toast.success("Dashboard created successfully");
      utils.analytics.dashboards.list.invalidate();
      setCreateOpen(false);
      setNewName("");
      setNewDescription("");
      // Navigate to the newly created dashboard
      if (data?.id) handleOpenDashboard(data.id);
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to create dashboard");
    },
  });

  const handleCreate = () => {
    if (!newName.trim()) {
      toast.error("Dashboard name is required");
      return;
    }
    createDashboard.mutate({
      name: newName.trim(),
      description: newDescription.trim() || undefined,
    });
  };

  const filtered = useMemo(() => {
    const all = dashboards ?? [];
    return all.filter((d) => {
      const matchSearch =
        d.name.toLowerCase().includes(search.toLowerCase()) ||
        (d.description ?? "").toLowerCase().includes(search.toLowerCase());
      const matchType =
        typeFilter === "all" ||
        (typeFilter === "system" ? d.isSystem : !d.isSystem);
      return matchSearch && matchType;
    });
  }, [dashboards, search, typeFilter]);

  const systemDashboards = [...prependSystem, ...filtered.filter((d) => d.isSystem)];

  const columns: FioriColumn<Dashboard>[] = useMemo(
    () => [
      {
        key: "name",
        header: t('analytics.dashboardName'),
        render: (d) => {
          const Icon = DASHBOARD_ICONS[d.name] ?? LayoutDashboard;
          return (
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-md bg-[#5B2C93]/10 flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-[#5B2C93]" />
              </div>
              <span className="font-medium text-[#5B2C93] text-sm">{d.name}</span>
            </div>
          );
        },
      },
      {
        key: "description",
        header: t('common.description'),
        render: (d) => (
          <span className="text-sm text-[#6B6B6B] line-clamp-1">
            {d.description ?? "—"}
          </span>
        ),
      },
      {
        key: "type",
        header: t('common.type'),
        width: "100px",
        render: (d) => (
          <FioriStatusBadge
            status={d.isSystem ? "info" : "success"}
            label={d.isSystem ? t('analytics.system') : t('analytics.custom')}
          />
        ),
      },
      {
        key: "refresh",
        header: t('analytics.refresh'),
        width: "90px",
        hideOnMobile: true,
        render: (d) => (
          <div className="flex items-center gap-1 text-xs text-[#6B6B6B]">
            <Clock className="w-3 h-3" />
            {REFRESH_LABELS[d.name] ?? "—"}
          </div>
        ),
      },
      {
        key: "kiosk",
        header: t('analytics.tags'),
        width: "80px",
        hideOnMobile: true,
        render: (d) =>
          d.name === "NOC Big Screen" ? (
            <Badge
              variant="outline"
              className="text-xs border-orange-300 text-orange-600"
            >
              Kiosk
            </Badge>
          ) : null,
      },
      {
        key: "actions",
        header: "",
        width: "48px",
        align: "right",
        render: () => <ChevronRight className="w-4 h-4 text-[#B0B0B0]" />,
      },
    ],
    []
  );

  const handleOpenDashboard = (id: number) => {
    if (onOpenDashboard) {
      onOpenDashboard(id);
    } else {
      setSelectedDashboardId(id);
    }
  };

  if (!onOpenDashboard && selectedDashboardId !== null) {
    return (
      <DashboardView
        dashboardId={selectedDashboardId}
        onBack={() => setSelectedDashboardId(null)}
      />
    );
  }

  const activeFilters =
    typeFilter !== "all"
      ? [
          {
            key: "type",
            label: `Type: ${typeFilter}`,
            onRemove: () => setTypeFilter("all"),
          },
        ]
      : [];

  return (
    <>
      <div className="space-y-5 p-4 md:p-6 md:pl-0">
        {/* Filter bar */}
        <FioriFilterBar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder={t('analytics.searchDashboards')}
          activeFilters={activeFilters}
          onClearAll={() => {
            setSearch("");
            setTypeFilter("all");
          }}
          filters={
            <div className="flex items-center gap-1.5">
              {(["all", "system", "custom"] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTypeFilter(t)}
                  className={`px-3 py-1.5 text-xs rounded-md border transition-colors capitalize ${
                    typeFilter === t
                      ? "bg-[#5B2C93] text-white border-[#5B2C93]"
                      : "bg-white text-[#6B6B6B] border-[#E0E0E0] hover:border-[#5B2C93]/40"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          }
          trailing={
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                disabled={isRefetching}
                className="gap-1.5"
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 ${isRefetching ? "animate-spin" : ""}`}
                />
                {t('common.refresh')}
              </Button>
              {/* Only show New Dashboard button if user has analytics:create */}
              {canCreate && (
                <Button
                  size="sm"
                  onClick={() => setCreateOpen(true)}
                  className="bg-[#5B2C93] hover:bg-[#3D1C5E] gap-1.5"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {t('analytics.newDashboard')}
                </Button>
              )}
            </div>
          }
        />

        {/* System Dashboards */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <h2 className="text-sm font-semibold text-[#2C2C2C]">
              {t('analytics.systemDashboards')}
            </h2>
            <Badge variant="secondary" className="text-xs">
              {systemDashboards.length}
            </Badge>
            <span className="text-xs text-[#6B6B6B]">
              {t('analytics.systemDashboardsDesc')}
            </span>
          </div>
          <FioriTable<Dashboard>
            columns={columns}
            data={systemDashboards}
            isLoading={isLoading}
            rowKey={(d) => d.id}
            onRowClick={(d) => handleOpenDashboard(d.id)}
            emptyIcon={<LayoutDashboard className="w-8 h-8 text-[#B0B0B0]" />}
            emptyTitle={t('analytics.noDashboards')}
            emptyDescription={t('analytics.noDashboardsDesc')}
            footerInfo={`Showing ${systemDashboards.length} system dashboard${systemDashboards.length !== 1 ? "s" : ""}`}
          />
        </div>

      </div>

      {/* New Dashboard Dialog — only accessible when canCreate */}
      {canCreate && (
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="sm:max-w-[480px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#2C2C2C]">
                <div className="w-8 h-8 rounded-md bg-[#5B2C93]/10 flex items-center justify-center">
                  <LayoutDashboard className="w-4 h-4 text-[#5B2C93]" />
                </div>
                {t('analytics.createDashboard')}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* Name */}
              <div className="space-y-1.5">
                <Label htmlFor="dash-name" className="text-sm font-medium text-[#2C2C2C]">
                  {t('analytics.dashboardName')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="dash-name"
                  placeholder="e.g. Weekly Security Overview"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                  className="border-[#E0E0E0] focus:border-[#5B2C93] focus:ring-[#5B2C93]/20"
                  autoFocus
                />
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <Label htmlFor="dash-desc" className="text-sm font-medium text-[#2C2C2C]">
                  {t('common.description')} <span className="text-[#9B9B9B] font-normal">({t('common.optional')})</span>
                </Label>
                <Textarea
                  id="dash-desc"
                  placeholder="Briefly describe what this dashboard tracks..."
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  rows={3}
                  className="border-[#E0E0E0] focus:border-[#5B2C93] focus:ring-[#5B2C93]/20 resize-none"
                />
              </div>

              {/* Info note */}
              <p className="text-xs text-[#9B9B9B] bg-[#F8F5FF] border border-[#E8DCFF] rounded-md px-3 py-2">
                After creation, you can add KPI widgets and reports to build your dashboard layout.
              </p>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => { setCreateOpen(false); setNewName(""); setNewDescription(""); }}
                disabled={createDashboard.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleCreate}
                disabled={createDashboard.isPending || !newName.trim()}
                className="bg-[#5B2C93] hover:bg-[#3D1C5E] min-w-[120px]"
              >
                {createDashboard.isPending ? t('common.loading') : t('analytics.createDashboard')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
